import {Component, OnInit, ViewChild} from '@angular/core';

import {ActivatedRoute, Router} from "@angular/router";
import {SpinnerComponent} from "@shared/components/spinner/spinner.component";
import {BreadcrumbService} from "@cdss/services/breadcrumb.service";
import {
    PatientRegistrationAccountSettingsComponent
} from "@cdss/pages/patient-registration/account-settings/patient-registration-account-settings.component";
import {
    PatientRegistrationPersonalInfoComponent
} from "@cdss/pages/patient-registration/personal-info/patient-registration-personal-info.component";
import {NgClass, NgIf} from "@angular/common";
import {Patient} from "@shared/api/models/query/patient.model";
import {PatientsService} from "@shared/api/services/v2/patients.service";
import {
    PatientRegistrationEdAndComorbiditiesComponent
} from "@cdss/pages/patient-registration/ed-and-comorbidities/patient-registration-ed-and-comorbidities.component";
import {
    PatientRegistrationCareProgrammeComponent,
} from "@cdss/pages/patient-registration/care-programme/patient-registration-care-programme.component";
import {
    PatientRegistrationConfirmationComponent
} from "@cdss/pages/patient-registration/confirmation/patient-registration-confirmation.component";
import {DepartmentsService} from "@shared/api/services/v2/departments.service";
import {ServicesService} from "@shared/api/services/v2/services.service";

@Component({
    selector: 'cdss-patient-registration-page',
    imports: [
        SpinnerComponent,
        PatientRegistrationAccountSettingsComponent,
        PatientRegistrationPersonalInfoComponent,
        NgIf,
        PatientRegistrationEdAndComorbiditiesComponent,
        NgClass,
        PatientRegistrationEdAndComorbiditiesComponent,
        PatientRegistrationEdAndComorbiditiesComponent,
        PatientRegistrationCareProgrammeComponent,
        PatientRegistrationEdAndComorbiditiesComponent,
        PatientRegistrationConfirmationComponent,
    ],
    templateUrl: './patient-registration.page.html',
    styleUrl: './patient-registration.page.scss'
})
export class PatientRegistrationPage implements OnInit {
    @ViewChild(PatientRegistrationAccountSettingsComponent) accountSettingsComponent: PatientRegistrationAccountSettingsComponent
    @ViewChild(PatientRegistrationPersonalInfoComponent) personalInfoComponent: PatientRegistrationPersonalInfoComponent
    @ViewChild(PatientRegistrationEdAndComorbiditiesComponent) edAndComorbiditiesComponent: PatientRegistrationEdAndComorbiditiesComponent
    @ViewChild(PatientRegistrationCareProgrammeComponent) careProgrammeComponent: PatientRegistrationCareProgrammeComponent
    @ViewChild(PatientRegistrationConfirmationComponent) confirmationComponent: PatientRegistrationConfirmationComponent

    loading: boolean = false

    patientId?: number
    patient?: Patient

    organisationId: number
    serviceId: number
    departmentId: number
    typeOfUser: string
    licenseId: number
    clinicianId: number | null = null
    isTest: boolean = false

    step: number = 1

    constructor(
        private breadcrumbService: BreadcrumbService,
        private router: Router,
        private activeRoute: ActivatedRoute,
        private patientsService: PatientsService,
        private departmentsService: DepartmentsService,
        private servicesService: ServicesService,
    ) {
    }

    ngOnInit(): void {
        this.breadcrumbService.set([
            {title: "Add new user", url: null},
        ])
        this.patientId = this.activeRoute.snapshot.params["id"]
        if (this.patientId) {
            this.initPatient()
        }
    }

    async initPatient(move: boolean = true) {
        this.loading = true;
        this.patient = await this.patientsService.getById(this.patientId)
        this.departmentId = this.patient.department.id
        this.licenseId = this.patient.user.activeLicense.license.id

        if (!this.organisationId) {
            const department = await this.departmentsService.getById(this.departmentId)
            const serviceId = department.serviceDepartments[0].service.id
            const service = await this.servicesService.getById(serviceId)
            this.organisationId = service.organisationServices[0].organisation.id
            this.serviceId = serviceId
        }

        if (this.patient.type) {
            this.typeOfUser = this.patient.type
        } else {
            // Fallback for patients registered before the type field was introduced
            const hasPatient = (this.patient.user.phoneNumber != null)
            const hasCarer = (this.patient.carer?.user?.phoneNumber != null)
            if (hasPatient && hasCarer) {
                this.typeOfUser = "Patient and carer"
            } else if (hasCarer) {
                this.typeOfUser = "Carer"
            } else {
                this.typeOfUser = "Patient"
            }
        }

        if (move) {
            this.moveToStep((this.patient.wizardStep ?? 2) + 1)
        }
        this.loading = false
    }

    nextStep() {
        switch (this.step) {
            case 1:
                this.accountSettingsComponent.processForm()
                break
            case 2:
                this.personalInfoComponent.processForm(false)
                break
            case 3:
                this.edAndComorbiditiesComponent.processForm(false)
                break
            case 4:
                this.careProgrammeComponent.processForm(false)
                break
            case 5:
                this.confirmationComponent.submit()
        }
    }

    draft() {
        switch (this.step) {
            case 2:
                this.personalInfoComponent.processForm(true)
                break
            case 3:
                this.edAndComorbiditiesComponent.processForm(true)
                break
            case 4:
                this.careProgrammeComponent.processForm(true)
                break
            case 5:
                this.confirmationComponent.submit()
        }
    }

    previousStep() {
        this.moveToStep(this.step - 1)
    }

    moveToStep(step: number) {
        this.step = step
        switch (step) {
            case 2:
                setTimeout(() => {
                    this.personalInfoComponent.init(this.typeOfUser, this.departmentId, this.licenseId, this.clinicianId, this.isTest, this.patient)
                }, 100)
                break
            case 3:
                setTimeout(() => {
                    this.edAndComorbiditiesComponent.init(this.patient, this.typeOfUser)
                }, 100)
                break
            case 4:
                setTimeout(() => {
                    this.careProgrammeComponent.init(this.patient, this.typeOfUser, false, this.organisationId)
                }, 100)
                break
            case 5:
                setTimeout(() => {
                    this.confirmationComponent.init(this.patient, this.typeOfUser)
                }, 100)
                break
        }
    }

    accountSettingsCompleted(accountSettings: {
        organisationId: number;
        serviceId: number;
        departmentId: number;
        typeOfUser: string;
        licenseId: number;
        clinicianId: number | null;
        isTest: boolean;
    }) {
        this.organisationId = accountSettings.organisationId
        this.serviceId = accountSettings.serviceId
        this.departmentId = accountSettings.departmentId
        this.typeOfUser = accountSettings.typeOfUser
        this.licenseId = accountSettings.licenseId
        this.clinicianId = accountSettings.clinicianId
        this.isTest = accountSettings.isTest

        this.moveToStep(2)
    }

    personalInfoCompleted(output: {patientId: number, draft: boolean}) {
        if (output.draft) {
            this.patientId = output.patientId
            this.initPatient(false).then(() => {
                this.personalInfoComponent.init(this.typeOfUser, this.departmentId, this.licenseId, this.clinicianId, this.isTest, this.patient)
            })
        } else {
            this.router.navigateByUrl('/cdss/patient-registration').then(() => {
                this.router.navigateByUrl('/cdss/patient-registration/' + output.patientId)
            })
        }
    }

    protected moveStepFromEdAndComorbidities(output: string) {
        if (output == "draft") {
            return
        }
        if (output == "next") {
            this.moveToStep(4)
        } else {
            this.moveToStep(2)
        }
    }

    protected moveStepFromCareProgramme(output: string) {
        if (output == "draft") {
            return
        }
        if (output == "next") {
            this.moveToStep(5)
        } else {
            this.moveToStep(3)
        }
    }

    protected moveStepFromConfirmation(output: string) {
        if (output == "next") {
            this.patientsService.update(this.patientId, {wizardStep: 4}).subscribe({
                next: () => {
                    this.router.navigateByUrl('/cdss/dashboard')
                }
            })
        } else {
            this.moveToStep(4)
        }
    }
}
